import React from 'react';
import { motion } from 'framer-motion';

const BodyMap = ({ selectedParts = [], onPartClick, plans = [] }) => {
  // Body parts with their SVG paths
  const bodyParts = {
    head: {
      label: 'Kafa',
      path: 'M150,30 C130,30 115,50 115,75 C115,100 130,115 150,115 C170,115 185,100 185,75 C185,50 170,30 150,30 Z',
      center: { x: 150, y: 72 }
    },
    neck: {
      label: 'Boyun',
      path: 'M140,115 L160,115 L165,135 L135,135 Z',
      center: { x: 150, y: 125 }
    },
    leftShoulder: {
      label: 'Sol Omuz',
      path: 'M100,135 L135,135 L135,155 L95,155 C90,155 88,150 90,145 Z',
      center: { x: 115, y: 145 }
    },
    rightShoulder: {
      label: 'Sağ Omuz',
      path: 'M165,135 L200,135 C210,145 208,155 205,155 L165,155 Z',
      center: { x: 185, y: 145 }
    },
    chest: {
      label: 'Göğüs',
      path: 'M135,135 L165,135 L165,200 L135,200 Z',
      center: { x: 150, y: 167 }
    },
    leftArm: {
      label: 'Sol Kol',
      path: 'M95,155 L100,155 L100,220 L85,220 L75,190 Z',
      center: { x: 90, y: 187 }
    },
    rightArm: {
      label: 'Sağ Kol',
      path: 'M200,155 L205,155 L225,190 L215,220 L200,220 Z',
      center: { x: 210, y: 187 }
    },
    leftForearm: {
      label: 'Sol Ön Kol',
      path: 'M85,220 L100,220 L100,280 L80,280 Z',
      center: { x: 90, y: 250 }
    },
    rightForearm: {
      label: 'Sağ Ön Kol',
      path: 'M200,220 L215,220 L220,280 L200,280 Z',
      center: { x: 210, y: 250 }
    },
    leftHand: {
      label: 'Sol El',
      path: 'M80,280 L100,280 L100,310 L75,310 Z',
      center: { x: 88, y: 295 }
    },
    rightHand: {
      label: 'Sağ El',
      path: 'M200,280 L220,280 L225,310 L200,310 Z',
      center: { x: 210, y: 295 }
    },
    stomach: {
      label: 'Karın',
      path: 'M135,200 L165,200 L170,260 L130,260 Z',
      center: { x: 150, y: 230 }
    },
    leftThigh: {
      label: 'Sol Uyluk',
      path: 'M130,260 L150,260 L145,340 L115,340 Z',
      center: { x: 135, y: 300 }
    },
    rightThigh: {
      label: 'Sağ Uyluk',
      path: 'M150,260 L170,260 L185,340 L155,340 Z',
      center: { x: 165, y: 300 }
    },
    leftCalf: {
      label: 'Sol Baldır',
      path: 'M115,340 L145,340 L140,420 L110,420 Z',
      center: { x: 127, y: 380 }
    },
    rightCalf: {
      label: 'Sağ Baldır',
      path: 'M155,340 L185,340 L190,420 L160,420 Z',
      center: { x: 172, y: 380 }
    },
    leftFoot: {
      label: 'Sol Ayak',
      path: 'M105,420 L140,420 L138,450 L100,450 Z',
      center: { x: 120, y: 435 }
    },
    rightFoot: {
      label: 'Sağ Ayak',
      path: 'M160,420 L195,420 L200,450 L162,450 Z',
      center: { x: 180, y: 435 }
    },
  };

  const getPlanForPart = (partKey) => {
    return plans.find(plan => plan.body_part === partKey);
  };

  return (
    <div className="relative">
      <svg
        viewBox="0 0 300 480"
        className="w-full max-w-md mx-auto"
        style={{ filter: 'drop-shadow(0 0 10px rgba(212, 255, 0, 0.1))' }}
      >
        {/* Background glow */}
        <defs>
          <radialGradient id="bodyGlow" cx="50%" cy="50%" r="50%" fx="50%" fy="50%">
            <stop offset="0%" stopColor="#D4FF00" stopOpacity="0.1" />
            <stop offset="100%" stopColor="#0a0a0a" stopOpacity="0" />
          </radialGradient>
          <filter id="glow">
            <feGaussianBlur stdDeviation="2" result="coloredBlur" />
            <feMerge>
              <feMergeNode in="coloredBlur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {/* Body parts */}
        {Object.entries(bodyParts).map(([key, part]) => {
          const isSelected = selectedParts.includes(key);
          const hasPlan = getPlanForPart(key);
          
          return (
            <motion.g
              key={key}
              whileHover={{ scale: 1.02 }}
              onClick={() => onPartClick?.(key, part.label)}
              style={{ cursor: 'pointer' }}
            >
              <motion.path
                d={part.path}
                fill={hasPlan ? 'rgba(212, 255, 0, 0.3)' : isSelected ? 'rgba(212, 255, 0, 0.2)' : 'rgba(42, 42, 42, 0.8)'}
                stroke={hasPlan ? '#D4FF00' : isSelected ? '#D4FF00' : '#3a3a3a'}
                strokeWidth={isSelected || hasPlan ? 2 : 1}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                whileHover={{ 
                  fill: 'rgba(212, 255, 0, 0.15)',
                  stroke: '#D4FF00'
                }}
                transition={{ duration: 0.2 }}
                data-testid={`body-part-${key}`}
              />
              {hasPlan && (
                <circle
                  cx={part.center.x}
                  cy={part.center.y}
                  r="8"
                  fill="#D4FF00"
                  className="animate-pulse"
                />
              )}
            </motion.g>
          );
        })}
      </svg>

      {/* Legend */}
      <div className="flex justify-center gap-6 mt-6 text-sm">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded-sm bg-muted border border-border" />
          <span className="text-muted-foreground">Boş</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded-sm bg-primary/30 border border-primary" />
          <span className="text-muted-foreground">Planlanmış</span>
        </div>
      </div>
    </div>
  );
};

export default BodyMap;
